import { useInfiniteQuery } from '@tanstack/react-query';

import { ProfileSection } from '@/components/profile/ProfileSection';
import { ProfileItemMeta } from '@/components/profile/ProfileText';
import { Link, LinkText } from '@/components/ui/link';
import { VStack } from '@/components/ui/vstack';
import {
  getUserPublicationsInfiniteQueryOptions,
  type Publication,
} from '@/services/getUserPublications';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { trackProfilePublicationOpened } from '@/util/analyticsEvents';
import { formatPublicationDate } from '@/util/formatPublicationDate';

interface PublicationsSectionProps {
  expertId: number;
  /**
   * Called when the user collapses an expanded list via "Show less". The
   * parent uses this to scroll the section header back into view on platforms
   * where DOM-based scrollIntoView is not available.
   */
  onCollapseScroll?: () => void;
}

/**
 * Builds the muted "journal · Mon YYYY" subline, omitting either part when the
 * source data is missing it.
 */
function buildMeta(publication: Publication): string {
  const date = formatPublicationDate(publication.year, publication.month);

  return [publication.journal, date].filter(Boolean).join(' · ');
}

/**
 * PublicationsSection - expert profile publications list.
 *
 * Owns its own query so loading, empty, and error states stay isolated from
 * the rest of the profile. Each row links out to PubMed or the publisher via
 * the item url, opening a new tab on web. An empty list is the expected normal
 * state while the source tables are still being populated.
 */
export function PublicationsSection({ expertId, onCollapseScroll }: PublicationsSectionProps) {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(getUserPublicationsInfiniteQueryOptions(expertId));

  const publications = data?.pages.flatMap((page) => page?.publications ?? []) ?? [];

  const total = data?.pages[0]?.total ?? publications.length;

  return (
    <ProfileSection<Publication>
      title="Publications"
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      items={publications}
      total={total}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={() => fetchNextPage()}
      emptyText="No publications listed yet."
      expertId={expertId}
      sectionKey="publications"
      onCollapseScroll={onCollapseScroll}
      keyExtractor={(publication) => String(publication.id)}
      renderItem={(publication) => {
        const meta = buildMeta(publication);

        return (
          <VStack className="gap-1">
            <Link
              href={publication.url}
              isExternal
              onPress={() =>
                trackProfilePublicationOpened({
                  expertId,
                  publicationId: publication.id,
                  publicationTitle: publication.title,
                  publicationYear: publication.year,
                })
              }
              {...getAccessibilityProps({
                role: 'link',
                label: `${publication.title}, opens in a new tab`,
              })}>
              <LinkText size="md" className="text-primary-600 data-[hover=true]:text-primary-700">
                {publication.title}
              </LinkText>
            </Link>
            {meta ? <ProfileItemMeta numberOfLines={1}>{meta}</ProfileItemMeta> : null}
          </VStack>
        );
      }}
    />
  );
}
